import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState
} from 'react';
import styles from './styles.module.scss';
import {MapWithDnd} from './components/Map';
import {TableWithDnd} from './components/Table';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import {DNDController} from './components/Table/DNDController';
import {APIProvider} from '@vis.gl/react-google-maps';
import {LocationType, MapSettings, MovingLocation} from './types';
import {PlannedHeader} from './components/PlannedHeader';
import {Header} from './components/Header';
import {AdvancedDeliveryFieldsEditor} from './components/FieldsEditor';
import {RoutesTotals} from './components/RoutesTotals/RoutesTotals';
import {AdvancedDeliveryContext} from './reducer/context';
import {useQuery} from 'react-query';
import {CircularLoader} from '../../components/CircularLoader/CircularLoader';
import {
    ADPFetchMax,
    ColumnsNamesForMarkers,
    ColumnsNamesForRoutes,
    DEFAULT_DELI_TITLE_COLUMNS,
    DEFAULT_DELI_TOTALS_COLUMNS,
    DEFAULT_PLANNED_COLUMNS,
    DEFAULT_TABLE_COLUMNS,
    DLVADDRESSSUM,
    DLVADDRESSSUMDET,
    DLVTRACKI,
    EditingFieldsType,
    OPTDATE,
    OPTIMIZED,
    PLANNED_COLS_WIDTH_DICT_STORE_KEY,
    ROUTE_TABLE_COLS_WIDTH_DICT_STORE_KEY,
    routeColors,
    TOTDOCS,
    ViewType
} from './constants';
import {
    addDocumentsToDeliveryPlan,
    changeDeliveryLocationsOrders,
    fetchSubform,
    loadADPForm,
    loadSubformsForSpecificRows,
    moveBetweenTrackingDocuments,
    moveToTrackingDocuments,
    refetchInTheBackground,
    startADPForms
} from './actions/action';
import {EmptyState} from './components/EmptyState';
import {ReactComponent as NotFoundPlanning} from '../../images/notFoundADP.svg';
import {ReactComponent as EmptyRoutes} from '../../images/RoutesEmptyState.svg';
import {ReactComponent as NotFoundRoutes} from '../../images/RoutesEmptySearch.svg';
import {Flex, Scrollbar, Text} from '@priority-software/priority-style-react';
import {texts} from './texts';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {
    filterRecordsByType,
    getABBRlabel,
    getSelectedEntityCols,
    getShowTotalsForAll,
    getViewTypeOptions,
    mappedColumnIds,
    prepareRowsToMarkersType,
    setVisibleColumns
} from './utils';
import {RouteTile} from './components/RouteTile';
import {SelectedEntity} from '../../components/EditingDialog';
import {
    getColumnsWidthByKey,
    getRouteShowType,
    getSelectedFields,
    getViewType,
    updateColsWidthDictionary,
    updateSelectedFields,
    updateShowTotalsForAll,
    updateViewType
} from './services/serverDataStore';
import {Point} from './components/Table/Point';
import {RoutesHeader} from './components/RoutesHeader';
import {getLang, isRTL} from '../../Localization';
import {fetchSysLangs} from '../../Localization/languages';
import {useScrollWrapDimensions} from '../../hooks/customScroll/useScrollWrapDimensions';
import {mapID} from './keys';
import {Form, formsStore, Record} from '../../stores/Form';
import {AvailableStatusesWrapper} from './components/StatusPicker';
import _ from 'lodash';
import {exitSubForm} from '../Form/actions/form';
import {WidthController} from './components/Table/WidthController';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Dictionary} from '../EOYWizard/types';
import {getEntDnameBySession, setIsNavigationEnabled} from '../../_shared/GWTBridge/invoke/genPage';
import {LocationType as DNDLocationType} from './components/Table/DNDController/DNDContext';
import {ModernTooltip} from '../../components/Common/Tooltip/ModernTooltip';
import {RoutesWrapper} from './components/RoutesWrapper';
import {
    OptimizationDialog,
    OptimizationDialogConsiderOption,
    OptimizationDialogOpenValue,
    OptimizationDialogRefType,
    OptimizationDialogState
} from './components/OptimizationDialog';
import {getGoogleMapsApiKey} from './utils/api/googleMaps';
import {OptimizationLoader, OptimizationLoaderState} from './components/OptimizationLoader';
import {OptimizationCancelDialog} from './components/OptimizationCancelDialog';

type Props = {
    onClose: () => void;
    zIndex: number;
    session: string;
    setIsScreenLocked: (isLocked: boolean) => void;
    optimizationLoaderState: OptimizationLoaderState;
    setOptimizationLoaderState: (value: OptimizationLoaderState) => void;
    showCloseWarning: boolean;
    setShowCloseWarning: (value: boolean) => void;
};

// height including header height and  table padding
const tableHeaderOffset = 128;
const handleCloseTimeoutDelay = 100;

export const AdvancedDelivery = observer(
    ({
        onClose,
        zIndex,
        session,
        setIsScreenLocked,
        optimizationLoaderState,
        setOptimizationLoaderState,
        showCloseWarning,
        setShowCloseWarning
    }: Props) => {
        const [viewType, setViewType] = useState<DropdownItemData>(getViewTypeOptions()?.[0]);
        const [selectedPlannedRows, setSelectedPlannedRows] = useState<Record[]>([]);
        const [isShowPlannedLocations, setIsShowPlannedLocations] = useState(false);
        const [isOpenEditFieldsDlg, setIsOpenEditFieldsDlg] = useState(false);
        const [planningFormEditEntity, setPlanningFormEditEntity] = useState<SelectedEntity>(null);
        const [routesFormEditEntity, setRoutesFormEditEntity] = useState<SelectedEntity>(null);
        const [routesTableEditEntity, setRoutesTableEditEntity] = useState<SelectedEntity>(null);
        const [routeTileTotalsEditEntity, setRouteTileTotalsEditEntity] =
            useState<SelectedEntity>(null);
        const [isRouteTileLoading, setIsRouteTileLoading] = useState(false);
        const [isRouteGroupLoading, setIsRouteGroupLoading] = useState(false);
        const [editingFieldsType, setEditingFieldsType] = useState<EditingFieldsType>(null);
        const [isRouteReloading, setIsRouteReloading] = useState(false);
        const [cachedDeliveryColumns, setCachedDeliveryColumns] =
            useState<priority.netitems.form.IColumn[]>();
        const [plannedColsWidth, setPlannedColsWidth] = useState<Dictionary<number>>({});
        const [routeTableColsWidth, setRouteTableColsWidth] = useState<Dictionary<number>>({});
        const [docTypeFilter, setDocTypeFilter] = useState<DropdownItemData[]>([]);
        const [isShowAllTotals, setIsShowAllTotals] = useState(true);
        const [lang, setLang] = useState(null);
        const optimizationDialogRef = useRef<OptimizationDialogRefType>();
        const [reloadingRoutesLocker, setReloadingRoutesLocker] = useState(false);
        const [routesReloadHaveStarted, setRoutesReloadHaveStarted] = useState(false);

        const googleMapsApiKey = useMemo(() => getGoogleMapsApiKey(), []);

        const [optimizationDialogOpenValue, setOptimizationDialogOpenValue] =
            useState<OptimizationDialogOpenValue>(OptimizationDialogOpenValue.Closed);

        const [optimizationDialogState, setOptimizationDialogState] =
            useState<OptimizationDialogState>({
                runOnlyOnSelected: true,
                optimizeByDistance: false,
                constraintsConsideration: true,
                timeWindowConsideration: true,
                unassignedOptimization: true,
                consider: OptimizationDialogConsiderOption.Route
            });

        const [mapSettings, setMapSettings] = useState<MapSettings>({
            showLine: true,
            showAddresses: false,
            showStartEnd: false
        });

        const PLANNED_BLOCK = useId();
        const DELIVERY_BLOCK = useId();

        const isRtl = isRTL();

        const dname = useMemo(() => getEntDnameBySession(session), [session]);

        const {setWrapRef, maxHeight: scrollMaxHeight} = useScrollWrapDimensions();
        const scrollMaxHeightWithOutHeader = scrollMaxHeight - tableHeaderOffset;

        const {
            planningForm,
            routesForm,
            handleAdpOpen,
            handlePlannedReload,
            handledByAdpHandler,
            changeHandledByAdpHandler,
            routes,
            closeADP,
            setRoutes,
            setIsCollapsableRouteOpen,
            setLocationSubformOpen,
            setRouteLocationsAndForm,
            setRouteLocationReload,
            selectedDocumentType,
            toggleRouteSelect,
            toggleDisplayedRoutes,
            unselectAllRoutes,
            showMaxRoutesCapReachedMessage,
            showMaxPlannedCapReachedMessage,
            showAll
        } = useContext(AdvancedDeliveryContext);

        const {columns: planningColumn, records: planningRecords} = planningForm || {};

        const {records: routesRecords, filters: routesFormFilters} = routesForm || {};

        useEffect(() => {
            getViewType().then(type => {
                setViewType(type);
            });
            getRouteShowType().then(type => {
                setDocTypeFilter(type);
            });
            getColumnsWidthByKey(PLANNED_COLS_WIDTH_DICT_STORE_KEY).then(plWidth => {
                setPlannedColsWidth(plWidth);
            });
            getColumnsWidthByKey(ROUTE_TABLE_COLS_WIDTH_DICT_STORE_KEY).then(rTWidth => {
                setRouteTableColsWidth(rTWidth);
            });
            getShowTotalsForAll().then(res => {
                setIsShowAllTotals(res);
            });
            fetchSysLangs(true).then(sysLangs => {
                if (sysLangs) {
                    const langId = getLang();
                    const res = sysLangs.find(item => item.lang === langId);
                    if (res) {
                        return setLang(res.googlelang);
                    }
                }
                setLang('en');
            });
        }, []);

        const {isLoading: isPlanningLoading} = useQuery(
            ['LoadingPlanningForm' + session],
            async () => await loadADPForm(startADPForms, dname),
            {
                enabled: !planningForm,
                onSuccess: data => handleAdpOpen(data)
            }
        );

        const {isLoading: isLoadingSelectedPlannedFields} = useQuery(
            ['LoadingSelectedPlannedFields' + session],
            async () => await getSelectedFields(EditingFieldsType.Planned, planningForm?.ename),
            {
                enabled: !planningFormEditEntity && !!planningForm,
                onSuccess: data =>
                    setPlanningFormEditEntity({
                        entityId: planningForm?.ename,
                        cols: planningForm?.columns || [],
                        selectedFields: data.selectedColumnsRes
                    })
            }
        );

        const {isLoading: isLoadingSelectedTitleFields} = useQuery(
            ['LoadingSelectedTitleFields' + session],
            async () => await getSelectedFields(EditingFieldsType.RouteTitle, routesForm?.ename),
            {
                enabled: !routesFormEditEntity && !!routesForm,
                onSuccess: data => {
                    setRoutesFormEditEntity({
                        entityId: routesForm?.ename,
                        cols: routesForm?.columns || [],
                        selectedFields: data.selectedColumnsRes
                    });
                }
            }
        );

        const {isLoading: isLoadingRouteTileTotalFields} = useQuery(
            ['LoadingRouteTileTotalFields' + session],
            async () =>
                await getSelectedFields(EditingFieldsType.RouteTileTotals, routesForm?.ename),
            {
                enabled: !routesFormEditEntity && !!routesForm,
                onSuccess: data => {
                    const cols = getSelectedEntityCols(
                        EditingFieldsType.RouteTileTotals,
                        routesForm?.columns
                    );
                    setRouteTileTotalsEditEntity({
                        entityId: EditingFieldsType.RouteTileTotals,
                        cols: cols || [],
                        selectedFields: data.selectedColumnsRes
                    });
                }
            }
        );

        const setDeliveryColumnsSetting = async cachedCols => {
            const res = await getSelectedFields(EditingFieldsType.RouteTable, DLVADDRESSSUMDET);
            setRoutesTableEditEntity({
                entityId: DLVADDRESSSUMDET,
                cols: cachedCols || [],
                selectedFields: res.selectedColumnsRes
            });
            setCachedDeliveryColumns(cachedCols);
        };

        const isLoadingEditingFields =
            isLoadingSelectedPlannedFields ||
            isLoadingSelectedTitleFields ||
            isLoadingRouteTileTotalFields;

        useEffect(() => {
            window.addEventListener('ADPLoadEnd', handleADPLoadEndEvent as EventListener);
            return () =>
                window.removeEventListener('ADPLoadEnd', handleADPLoadEndEvent as EventListener);
        }, [planningForm, routesForm]);

        useEffect(() => {
            if (routesRecords?.length === ADPFetchMax && routesForm.hasMore) {
                if (handledByAdpHandler) {
                    changeHandledByAdpHandler(false);
                } else {
                    showMaxRoutesCapReachedMessage();
                }
            }
        }, [routesRecords]);

        useEffect(() => {
            if (planningRecords?.length === ADPFetchMax && planningForm.hasMore) {
                if (handledByAdpHandler) {
                    changeHandledByAdpHandler(false);
                } else {
                    showMaxPlannedCapReachedMessage();
                }
            }
        }, [planningRecords]);

        const handleADPLoadEndEvent = (event: CustomEvent<any>) => {
            lockAndReloadPlanningForm();
        };

        const lockAndReloadPlanningForm = async () => {
            setIsScreenLocked(true);
            await refetchInTheBackground(planningForm);
            handlePlannedReload(planningForm);
            setIsScreenLocked(false);
        };

        const reloadForms = async (refetchRoutes: boolean = false) => {
            await Promise.all([
                ...(refetchRoutes === false ? [] : [refetchInTheBackground(routesForm, false)]),
                refetchInTheBackground(planningForm)
            ]);
            return {planningForm, routesForm};
        };

        const removeLocationsCacheFromClosedRoutes = () => {
            for (const route of routes.filter(
                x => !x.locationSubformOpen && x.locationSubform !== null
            )) {
                const id = Number.parseInt(route.id);
                setLocationSubformOpen(id, false);
                setRouteLocationsAndForm(id, [], null);
            }
        };

        const processBatchRoutesReloadData = async (data: Dictionary<Dictionary<Form>>) => {
            const recordIndexes = Object.keys(data).map(x => Number.parseInt(x));
            for (let i = 0; i < recordIndexes.length; i++) {
                const id = recordIndexes[i];
                const form = data[id][DLVADDRESSSUM];
                const locations: LocationType[] = form.records
                    .filter(x => !x.isNew)
                    .map(location => ({
                        id: location.recordIndex,
                        data: location,
                        itemsSubform: null
                    }));
                setRouteLocationsAndForm(id, locations, form);
            }
        };

        const batchRoutesReload = async (ids: string[]) => {
            const recordIndexes = ids.map(x => Number.parseInt(x));
            const result = await loadSubformsForSpecificRows(
                routesForm,
                [DLVADDRESSSUM],
                recordIndexes,
                false
            );
            await processBatchRoutesReloadData(result);
        };

        const reloadRoutesPerDate = async (date: string) => {
            if (date) {
                await batchRoutesReload(
                    routes
                        .map(route => {
                            const routeDate = route.data.getField(
                                routesForm.getColumnByName('DISTRDATE')?.id
                            )?.currentValue as string;
                            if (routeDate === date) {
                                return route.id;
                            }
                            return null;
                        })
                        .filter(Boolean)
                );
            } else {
                removeLocationsCacheFromClosedRoutes();
                const routesToReload = routes
                    .map(route => {
                        if (route.locationSubformOpen) {
                            return route.id;
                        }
                        return null;
                    })
                    .filter(Boolean);
                await batchRoutesReload(routesToReload);
            }
        };

        const onAutomaticSchedulingHandler = async (date: string) => {
            setIsRouteReloading(true);
            await reloadForms(true);
            await reloadRoutesPerDate(date);
            setIsRouteReloading(false);
        };

        const refreshForms = async () => {
            setIsScreenLocked(true);
            changeHandledByAdpHandler(true);
            await loadADPForm(async () => {
                const data = await reloadForms(true);
                handleAdpOpen(data);
                return data;
            }, dname);
            setIsScreenLocked(false);
        };

        const updateFields = async (entityId: string, fields: Array<string>, cbc?: () => void) => {
            let form: Form = null;
            let type: EditingFieldsType = null;
            let storeColsWidthKey = null;
            let colsWidthDictionary: Dictionary<number> = null;
            let colWidthSetter: Dispatch<SetStateAction<Dictionary<number>>> = null;
            let handler: Dispatch<SetStateAction<SelectedEntity>> = null;
            if (entityId === EditingFieldsType.Planned) {
                form = planningForm;
                type = EditingFieldsType.Planned;
                colsWidthDictionary = plannedColsWidth;
                storeColsWidthKey = PLANNED_COLS_WIDTH_DICT_STORE_KEY;
                colWidthSetter = setPlannedColsWidth;
                handler = setPlanningFormEditEntity;
            } else if (entityId === EditingFieldsType.RouteTitle) {
                form = routesForm;
                type = EditingFieldsType.RouteTitle;
                handler = setRoutesFormEditEntity;
            } else if (entityId === EditingFieldsType.RouteTileTotals) {
                form = routesForm;
                type = EditingFieldsType.RouteTileTotals;
                handler = setRouteTileTotalsEditEntity;
            } else if (entityId === EditingFieldsType.RouteTable) {
                form = new Form(
                    DLVADDRESSSUMDET,
                    {Session: '1', Title: ''},
                    {Columns: cachedDeliveryColumns || []},
                    2
                );
                type = EditingFieldsType.RouteTable;
                colsWidthDictionary = routeTableColsWidth;
                storeColsWidthKey = ROUTE_TABLE_COLS_WIDTH_DICT_STORE_KEY;
                colWidthSetter = setRouteTableColsWidth;
                handler = setRoutesTableEditEntity;
            }
            updateSelectedFields(fields, form?.ename, type, false);
            const cols = getSelectedEntityCols(type, form?.columns);

            if (colsWidthDictionary) {
                const updatedDictionary = await updateColsWidthDictionary(
                    storeColsWidthKey,
                    colsWidthDictionary,
                    fields,
                    cols
                );
                colWidthSetter(updatedDictionary);
            }

            handler({
                entityId,
                cols,
                selectedFields: fields
            });
            cbc && cbc();
        };

        const handleCloseEditFieldsDlg = () => {
            setIsNavigationEnabled(true);
            setIsOpenEditFieldsDlg(false);
        };

        const onOpenEditFieldsDlg = useCallback(() => {
            setIsNavigationEnabled(false);
            setIsOpenEditFieldsDlg(true);
        }, []);

        const onOpenFieldsEditor = (type: EditingFieldsType) => {
            setEditingFieldsType(type);
            onOpenEditFieldsDlg();
        };

        const getEntityByType = useCallback(() => {
            let entity;
            if (editingFieldsType === EditingFieldsType.Planned) {
                entity = {
                    selectedEntity: planningFormEditEntity,
                    defaultFields: DEFAULT_PLANNED_COLUMNS,
                    helpText: texts.planned_help_text,
                    dialogTitle: `${texts.select_fields} ${texts.planned_title}`
                };
            } else if (editingFieldsType === EditingFieldsType.RouteTitle) {
                entity = {
                    selectedEntity: routesFormEditEntity,
                    defaultFields: DEFAULT_DELI_TITLE_COLUMNS,
                    helpText: texts.route_title_help_text,
                    dialogTitle: `${texts.select_fields} ${texts.route_title_title}`
                };
            } else if (editingFieldsType === EditingFieldsType.RouteTileTotals) {
                entity = {
                    selectedEntity: routeTileTotalsEditEntity,
                    defaultFields: DEFAULT_DELI_TOTALS_COLUMNS,
                    helpText: texts.route_totals_help_text,
                    dialogTitle: `${texts.select_fields} ${texts.route_totals_title}`
                };
            } else if (editingFieldsType === EditingFieldsType.RouteTable) {
                entity = {
                    selectedEntity: routesTableEditEntity,
                    defaultFields: DEFAULT_TABLE_COLUMNS,
                    helpText: texts.route_details_help_text,
                    dialogTitle: `${texts.select_fields_for} ${texts.route_details_title}`
                };
            }
            return entity;
        }, [
            editingFieldsType,
            planningFormEditEntity,
            routesFormEditEntity,
            routeTileTotalsEditEntity,
            routesTableEditEntity
        ]);

        const filteredPlanningRecords = useMemo(() => {
            if (!planningRecords) return [];
            setSelectedPlannedRows([]);
            return planningRecords.filter(record => !record.isNew);
        }, [planningRecords]);

        const filteredRoutesRecords = useMemo(() => {
            if (!routesRecords) return [];

            const routesColumn = routesForm.getColumnByName(TOTDOCS);
            if (!routesColumn) return [];

            const filteredRoutesRecords = filterRecordsByType(
                routesRecords,
                routesColumn.id,
                selectedDocumentType
            );
            return filteredRoutesRecords;
        }, [routesRecords, selectedDocumentType]);

        useEffect(() => {
            const isEqual = _.isEqual(
                routes.map(x => x.id),
                filteredRoutesRecords.map(x => x.recordIndex.toString())
            );
            setRoutes(
                isEqual
                    ? routes.map(route => ({
                          ...route,
                          data: filteredRoutesRecords.find(
                              x => x.recordIndex.toString() === route.id
                          )
                      }))
                    : filteredRoutesRecords.map(routeRecord => ({
                          id: routeRecord.recordIndex.toString(),
                          color: routeColors[routeRecord.recordIndex % routeColors.length],
                          markers: [],
                          isShow: true,
                          data: routeRecord,
                          locations: undefined,
                          locationSubform: null,
                          locationSubformLoading: false,
                          isCollapsableOpen: false,
                          locationSubformOpen: false,
                          locationSubformReload: false,
                          shouldDisplay: true
                      })),
                isEqual ? showAll : true
            );
        }, [filteredRoutesRecords]);

        const handleClose = () => {
            setOptimizationLoaderState(OptimizationLoaderState.Hidden);

            // Delay to allow loader state to update before closing the dialog
            setTimeout(() => {
                optimizationDialogRef.current?.closeStream();
                onClose();
                closeADP();
            }, handleCloseTimeoutDelay);
        };

        const handleChooseView = (data, isSelected, name) => {
            if (isSelected) {
                setViewType(data);
                updateViewType(data.value);
            }
        };
        const contentView = classNames(styles.content, {
            [styles.map]: viewType.value === ViewType.MAP,
            [styles.routesMap]: viewType.value === ViewType.ROUTES_MAP,
            [styles.plannedMap]: viewType.value === ViewType.PLANNED_MAP,
            [styles.onlyRoutes]: viewType.value === ViewType.ONLY_ROUTES,
            [styles.onlyPlanned]: viewType.value === ViewType.ONLY_PLANNED
        });

        const plannedColumnsIds = useMemo(() => mappedColumnIds(planningColumn), [planningColumn]);
        //const routesColumnsIds = useMemo(() => mappedColumnIds(routesColumn), [routesColumn]);

        const onSelectLocations = useCallback(
            (rows: number[], id: string) => {
                const selectedRows = filteredPlanningRecords.filter(row =>
                    rows.includes(row.recordIndex)
                );
                setSelectedPlannedRows(selectedRows);
            },
            [filteredPlanningRecords, plannedColumnsIds]
        );

        const toggleShowLocation = useCallback(() => {
            setIsShowPlannedLocations(prev => !prev);
        }, []);

        const closeRoutesAndRemoveCache = () => {
            setRoutes(
                routes.map(route => ({
                    ...route,
                    shouldDisplay: true,
                    isSelected: false,
                    locationSubform: null,
                    isCollapsableOpen: false,
                    locationSubformOpen: false,
                    locations: undefined
                })),
                true
            );
        };

        const reloadSpecificRouteTables = async (
            ids: string[],
            ignoreRoutesReload: boolean = false
        ) => {
            if (!ignoreRoutesReload) {
                await refetchInTheBackground(routesForm, false);
            }
            for (const route of routes.filter(x =>
                ids.includes(
                    x.data.getField(routesForm.getColumnByName('DLVTRACKNUM')?.id)
                        ?.currentValue as string
                )
            )) {
                const id = Number.parseInt(route.id);
                setRouteLocationReload(id, true);
            }
        };

        const assignToRouteHandler = async (route: DropdownItemData) => {
            setIsScreenLocked(true);
            const routeId = route?.value;
            await moveToTrackingDocuments(route.value as string, selectedPlannedRows, dname);
            await reloadSpecificRouteTables([routeId.toString()]);
            await reloadForms(false);
            setIsScreenLocked(false);
        };

        const onDragEndHandler = useCallback(
            async (
                from: DNDLocationType,
                to: DNDLocationType,
                draggedRecords: Record[],
                originalMovingRecords: any[]
            ) => {
                setIsScreenLocked(true);

                try {
                    if (
                        from.id.startsWith(MovingLocation.table2) &&
                        to.id === MovingLocation.table1
                    ) {
                        await moveBetweenTrackingDocuments(
                            from.id.substring(MovingLocation.table2.length),
                            null,
                            draggedRecords,
                            dname
                        );
                        await reloadSpecificRouteTables([
                            from.id.substring(MovingLocation.table2.length)
                        ]);
                        await reloadForms(false);
                    } else if (
                        from.id === MovingLocation.table1 &&
                        to.id.startsWith(MovingLocation.table2)
                    ) {
                        const trackingDocId: string = to.id.substring(MovingLocation.table2.length);
                        await moveToTrackingDocuments(trackingDocId, draggedRecords, dname);
                        await reloadSpecificRouteTables([
                            to.id.substring(MovingLocation.table2.length)
                        ]);
                        await reloadForms(false);
                    } else if (
                        from.id.startsWith(MovingLocation.table2) &&
                        to.id.startsWith(MovingLocation.table2)
                    ) {
                        if (from.id !== to.id || (from.group && from.group !== to.group)) {
                            let ignoreRoutesReload = false;
                            if (from.id !== to.id) {
                                await moveBetweenTrackingDocuments(
                                    from.id.substring(MovingLocation.table2.length),
                                    to.id.substring(MovingLocation.table2.length),
                                    draggedRecords,
                                    dname
                                );
                            } else {
                                const routeId = getRouteIdByTableId(to.id);
                                let toRouteGuid = '';
                                if (Number.parseInt(to.group) >= 0) {
                                    const toLocation =
                                        routes[routeId].locations[Number.parseInt(to.group)];
                                    const toLocationRecord = toLocation.data;
                                    toRouteGuid = toLocationRecord.getField(
                                        toLocationRecord.parentForm.getColumnByName('KLINE')?.id
                                    )?.currentValue as string;
                                }

                                let fromRouteGuids = [];
                                if (draggedRecords.length) {
                                    fromRouteGuids = draggedRecords
                                        .map(
                                            record =>
                                                record.getField(
                                                    record.parentForm.getColumnByName('KLINE')?.id
                                                )?.currentValue as string
                                        )
                                        .filter(Boolean);
                                } else {
                                    const fromLocation =
                                        routes[routeId].locations[Number.parseInt(from.group)];
                                    const fromLocationRecord = fromLocation.data;
                                    const fromRouteGuid = fromLocationRecord.getField(
                                        fromLocationRecord.parentForm.getColumnByName('KLINE')?.id
                                    )?.currentValue as string;
                                    fromRouteGuids = [fromRouteGuid];
                                }

                                await changeDeliveryLocationsOrders(
                                    from.id.substring(MovingLocation.table2.length),
                                    fromRouteGuids,
                                    toRouteGuid,
                                    dname
                                );
                                const OptDateColumnId = routes[
                                    routeId
                                ].data.parentForm.columns.find(x => x.Name === OPTDATE)?.id;
                                const OptimizedColumnId = routes[
                                    routeId
                                ].data.parentForm.columns.find(x => x.Name === OPTIMIZED)?.id;
                                if (
                                    OptDateColumnId === undefined ||
                                    OptimizedColumnId === undefined
                                ) {
                                    ignoreRoutesReload = true;
                                } else {
                                    if (
                                        !routes[routeId].data.getField(OptimizedColumnId).Value
                                            .Boolean
                                    ) {
                                        ignoreRoutesReload = true;
                                    }
                                }
                            }
                            await reloadSpecificRouteTables(
                                [
                                    from.id.substring(MovingLocation.table2.length),
                                    to.id.substring(MovingLocation.table2.length)
                                ],
                                ignoreRoutesReload
                            );
                        }
                    } else if (from.id === MovingLocation.map || to.id === MovingLocation.map) {
                        if (from.id === MovingLocation.map && to.id !== MovingLocation.map) {
                            if (originalMovingRecords) {
                                if (to.id.startsWith(MovingLocation.table2)) {
                                    const routeId = (originalMovingRecords[0] as any).route.id;
                                    const fromId = routes[routeId].data.getField(
                                        routesForm.getColumnByName('DLVTRACKNUM')?.id
                                    )?.currentValue as string;
                                    await moveBetweenTrackingDocuments(
                                        fromId,
                                        to.id.substring(MovingLocation.table2.length),
                                        draggedRecords,
                                        dname
                                    );
                                    await reloadSpecificRouteTables([
                                        fromId,
                                        to.id.substring(MovingLocation.table2.length)
                                    ]);
                                } else if (to.id.startsWith(MovingLocation.table1)) {
                                    const routeId = (originalMovingRecords[0] as any).route.id;
                                    const fromId = routes[routeId].data.getField(
                                        routesForm.getColumnByName('DLVTRACKNUM')?.id
                                    )?.currentValue as string;
                                    await moveBetweenTrackingDocuments(
                                        fromId,
                                        null,
                                        draggedRecords,
                                        dname
                                    );
                                    await reloadSpecificRouteTables([fromId]);
                                    await reloadForms(false);
                                }
                            } else {
                                if (to.id.startsWith(MovingLocation.table2)) {
                                    const toId = to.id.substring(MovingLocation.table2.length);
                                    await moveToTrackingDocuments(toId, draggedRecords, dname);
                                    await reloadSpecificRouteTables([toId]);
                                    await reloadForms(false);
                                }
                            }
                        } else if (to.id.startsWith(MovingLocation.map)) {
                            if (
                                from.id.startsWith(MovingLocation.map) ||
                                from.id.startsWith(MovingLocation.table2)
                            ) {
                                let fromRoute: string | undefined, fromLocation: number | undefined;

                                if (from.id.startsWith(MovingLocation.map)) {
                                    fromRoute = from.group;
                                    fromLocation = from.row;
                                } else {
                                    const fromRouteId = from.id.substring(
                                        MovingLocation.table2.length
                                    );
                                    const routeObj = routes.find(
                                        x =>
                                            (x.data.getField(
                                                routesForm.getColumnByName('DLVTRACKNUM')?.id
                                            )?.currentValue as string) === fromRouteId
                                    );
                                    fromRoute = routeObj.id;
                                    fromLocation = Number.parseInt(from.group) + 1;
                                }

                                const toRoute = to.group;
                                const toLocation = to.row;

                                if (
                                    fromRoute === toRoute
                                        ? fromLocation && toLocation && fromLocation !== toLocation
                                        : toLocation
                                ) {
                                    const toId = routes[toRoute].data.getField(
                                        routesForm.getColumnByName('DLVTRACKNUM')?.id
                                    )?.currentValue as string;
                                    if (
                                        Number.parseInt(fromRoute) >= 0 &&
                                        Number.isInteger(fromLocation)
                                    ) {
                                        const fromId = routes[fromRoute].data.getField(
                                            routesForm.getColumnByName('DLVTRACKNUM')?.id
                                        )?.currentValue as string;
                                        if (fromRoute !== toRoute) {
                                            await moveBetweenTrackingDocuments(
                                                fromId,
                                                toId,
                                                draggedRecords,
                                                dname
                                            );
                                            await reloadSpecificRouteTables([fromId, toId]);
                                        } else {
                                            let toRouteGuid = '';
                                            if (Number.parseInt(toRoute) >= 0) {
                                                const toLocationObj =
                                                    routes[toRoute].locations[toLocation - 1];
                                                const toLocationRecord = toLocationObj.data;
                                                toRouteGuid = toLocationRecord.getField(
                                                    toLocationRecord.parentForm.getColumnByName(
                                                        'KLINE'
                                                    )?.id
                                                )?.currentValue as string;
                                            }

                                            let fromRouteGuids = [];
                                            if (
                                                draggedRecords.length &&
                                                from.id.startsWith(MovingLocation.table2)
                                            ) {
                                                fromRouteGuids = draggedRecords.map(
                                                    record =>
                                                        record.getField(
                                                            record.parentForm.getColumnByName(
                                                                'KLINE'
                                                            )?.id
                                                        )?.currentValue as string
                                                );
                                            } else {
                                                const fromLocationObj =
                                                    routes[toRoute].locations[fromLocation - 1];
                                                const fromLocationRecord = fromLocationObj.data;
                                                fromRouteGuids = [
                                                    fromLocationRecord.getField(
                                                        fromLocationRecord.parentForm.getColumnByName(
                                                            'KLINE'
                                                        )?.id
                                                    )?.currentValue as string
                                                ];
                                            }

                                            await changeDeliveryLocationsOrders(
                                                fromId,
                                                fromRouteGuids,
                                                toRouteGuid,
                                                dname
                                            );

                                            let ignoreRoutesReload = false;

                                            const OptDateColumnId = routes[
                                                toRoute
                                            ].data.parentForm.columns.find(
                                                x => x.Name === OPTDATE
                                            )?.id;
                                            const OptimizedColumnId = routes[
                                                toRoute
                                            ].data.parentForm.columns.find(
                                                x => x.Name === OPTIMIZED
                                            )?.id;
                                            if (
                                                OptDateColumnId === undefined ||
                                                OptimizedColumnId === undefined
                                            ) {
                                                ignoreRoutesReload = true;
                                            } else {
                                                if (
                                                    !routes[toRoute].data.getField(
                                                        OptimizedColumnId
                                                    ).Value.Boolean
                                                ) {
                                                    ignoreRoutesReload = true;
                                                }
                                            }

                                            await reloadSpecificRouteTables(
                                                [fromId],
                                                ignoreRoutesReload
                                            );
                                        }
                                    } else {
                                        await moveToTrackingDocuments(toId, draggedRecords, dname);
                                        await reloadSpecificRouteTables([toId]);
                                        await reloadForms(false);
                                    }
                                }
                            } else if (from.id.startsWith(MovingLocation.table1) && to.group) {
                                const toId = routes[to.group].data.getField(
                                    routesForm.getColumnByName('DLVTRACKNUM')?.id
                                )?.currentValue as string;
                                await moveToTrackingDocuments(toId, draggedRecords, dname);
                                await reloadSpecificRouteTables([toId]);
                                await reloadForms(false);
                            }
                        }
                    }
                } finally {
                    setIsScreenLocked(false);
                }
            },
            [reloadForms]
        );

        const getGroupRecords = async (
            id: number,
            records: Record[],
            groupIdx?: string
        ): Promise<Record[]> => {
            setIsScreenLocked(true);

            let locations = [];
            let form = null;
            if (records?.length) {
                for (let i = 0; i < records.length; i++) {
                    const record = records[i];
                    const groupIndex =
                        record.recordIndex < 0
                            ? Math.abs(record.recordIndex) - 1
                            : record.recordIndex;
                    if (!routes[id].locations[groupIndex].itemsSubform) {
                        routesForm.setCurrentRow(id);

                        if (!form) {
                            form = await fetchSubform(routesForm, DLVADDRESSSUM);
                        }
                        form.setCurrentRow(routes[id].locations[groupIndex].id);

                        locations = await fetchAddressDetails(
                            form,
                            id,
                            groupIndex.toString(),
                            locations
                        );
                        formsStore.getScreenBySession(routesForm.session).setActiveLevel(0);
                    } else {
                        locations = [
                            ...locations,
                            ...[routes[id].locations[groupIndex.toString()]]
                        ];
                    }
                }

                if (form) {
                    await exitSubForm(form.session);
                }
            } else if (groupIdx) {
                if (!routes[id].locations[groupIdx].itemsSubform) {
                    routesForm.setCurrentRow(id);

                    form = await fetchSubform(routesForm, DLVADDRESSSUM);
                    form.setCurrentRow(routes[id].locations[groupIdx].id);

                    locations = await fetchAddressDetails(form, id, groupIdx, locations);
                    await exitSubForm(form.session);
                    formsStore.getScreenBySession(routesForm.session).setActiveLevel(0);
                } else {
                    locations = [...locations, ...[routes[id].locations[groupIdx]]];
                }
            }

            setIsScreenLocked(false);
            return locations.flatMap(x => x.items.map(x => x.data));
        };

        const fetchAddressDetails = async (
            form: Form,
            id: number,
            groupIdx: string,
            locations: LocationType[]
        ) => {
            const subform = await fetchSubform(form, DLVADDRESSSUMDET, [DLVTRACKI]);

            locations = [...locations, ...[routes[id].locations[groupIdx]]];

            locations[locations.length - 1].itemsSubform = subform;
            locations[locations.length - 1].items = subform.records
                .filter(x => !x.isNew)
                .map(item => ({
                    id: item.recordIndex,
                    data: item
                }));

            await exitSubForm(subform.session);
            return locations;
        };

        const getRouteIdByTableId = (tableId: string): number | undefined => {
            const id = routes.find(
                x =>
                    (x.data.getField(routesForm.getColumnByName('DLVTRACKNUM')?.id)
                        ?.currentValue as string) ===
                    tableId.substring(MovingLocation.table2.length)
            )?.id;

            return id === undefined ? undefined : Number.parseInt(id);
        };

        const toggleHideRoutes = () => {
            toggleDisplayedRoutes(!showAll);
        };

        const setExpandMode = (showTotals: boolean) => {
            routes.forEach(route => {
                const routeId = Number(route.id);
                setIsCollapsableRouteOpen(routeId, showTotals);
                setLocationSubformOpen(routeId, false);
            });
        };

        const onToggleShowTotalsForAll = async () => {
            await updateShowTotalsForAll(!isShowAllTotals);
            setIsShowAllTotals(!isShowAllTotals);
        };

        const getRoutesEmptyStateTitle = () => {
            const text = texts.empty_state_routes;
            const newText = `${texts.new} `;
            const assign = texts.unassigned_optimization_title;
            const regex = new RegExp(`(${newText}|${assign})`, 'g');
            const titleParts = text.split(regex);
            const content = titleParts.map((part, idx) => {
                return (
                    <Text
                        tag="span"
                        key={idx}
                        fontWeight={part === assign || part === newText ? 'bold' : 'normal'}
                    >
                        {part}
                    </Text>
                );
            });
            return <Text skin="paragraph-1">{content}</Text>;
        };

        const assignToDeliveryHandler = async (date: string) => {
            setIsScreenLocked(true);
            setOptimizationDialogOpenValue(OptimizationDialogOpenValue.Closed);
            await addDocumentsToDeliveryPlan(date, selectedPlannedRows, dname);
            await onAutomaticSchedulingHandler(date);
            setIsScreenLocked(false);
        };

        const handleOptimizationDialogOpen = useCallback(
            (openValue: OptimizationDialogOpenValue) => {
                setIsNavigationEnabled(openValue === OptimizationDialogOpenValue.Closed);
                setOptimizationDialogOpenValue(openValue);
            },
            []
        );

        const selectedRoutes = routes?.filter(r => r.shouldDisplay).filter(x => x.isSelected) || [];

        const optimizationLoaderOnCloseProceed = (obj?: OptimizationDialogRefType) => {
            obj?.closeStream();
            setOptimizationLoaderState(OptimizationLoaderState.Hidden);
        };

        useEffect(() => {
            if (reloadingRoutesLocker) {
                if (routes.find(x => x.locationSubformReload)) {
                    setIsScreenLocked(true);
                    if (!routesReloadHaveStarted) {
                        setRoutesReloadHaveStarted(true);
                    }
                } else if (routesReloadHaveStarted) {
                    setReloadingRoutesLocker(false);
                    setIsScreenLocked(false);
                    setRoutesReloadHaveStarted(false);
                }
            }
        }, [routes, reloadingRoutesLocker]);

        return (
            <div className={styles.wrapper} style={{zIndex}}>
                <Header
                    onClose={handleClose}
                    chooseView={handleChooseView}
                    view={viewType}
                    refreshForms={refreshForms}
                    zIndex={zIndex}
                    session={session}
                    closeRoutesAndRemoveCache={closeRoutesAndRemoveCache}
                />
                <div className={contentView} ref={setWrapRef}>
                    <DNDController
                        onDragEndHandler={onDragEndHandler}
                        getGroupRecords={getGroupRecords}
                        getRouteIdByTableId={getRouteIdByTableId}
                    >
                        {isPlanningLoading || isLoadingEditingFields ? (
                            <CircularLoader />
                        ) : (
                            <div
                                className={classNames(styles.block, styles.tableBlock)}
                                id={PLANNED_BLOCK}
                            >
                                <PlannedHeader
                                    portalId={PLANNED_BLOCK}
                                    form={planningForm}
                                    records={filteredPlanningRecords}
                                    toggleShowLocations={toggleShowLocation}
                                    assignToRouteHandler={assignToRouteHandler}
                                    onOpenEditDialog={() =>
                                        onOpenFieldsEditor(EditingFieldsType.Planned)
                                    }
                                    selectedPlannedRows={selectedPlannedRows}
                                    setIsScreenLocked={setIsScreenLocked}
                                    unselectAll={() => setSelectedPlannedRows([])}
                                    routes={routes}
                                    routeTitleFields={routesFormEditEntity?.selectedFields}
                                    handleOptimizationDialogOpen={handleOptimizationDialogOpen}
                                    dname={dname}
                                />
                                <WidthController
                                    widthDictionary={plannedColsWidth}
                                    storeKey={PLANNED_COLS_WIDTH_DICT_STORE_KEY}
                                >
                                    <TableWithDnd
                                        selectedRows={selectedPlannedRows}
                                        height={scrollMaxHeightWithOutHeader}
                                        contentClassName={styles.plannedTableContent}
                                        onSelectRows={onSelectLocations}
                                        form={planningForm}
                                        id={MovingLocation.table1}
                                        setIsScreenLocked={setIsScreenLocked}
                                        columns={
                                            setVisibleColumns(
                                                planningForm,
                                                planningFormEditEntity?.selectedFields
                                            ) || []
                                        }
                                        emptyState={
                                            <EmptyState
                                                containerClasses={styles.emptyStateContainer}
                                                wrapperStyle={{
                                                    height: scrollMaxHeightWithOutHeader
                                                }}
                                                icon={<NotFoundPlanning />}
                                                title={
                                                    <Text skin="paragraph-1">
                                                        {texts.not_found_planned}
                                                    </Text>
                                                }
                                            />
                                        }
                                        records={filteredPlanningRecords}
                                        pinnedColumns={[]}
                                        getCellValue={(columnIndex, recordIndex) =>
                                            filteredPlanningRecords[recordIndex]?.getField(
                                                columnIndex
                                            )?.currentValue as string
                                        }
                                        renderCell={(columnIndex, recordIndex) =>
                                            filteredPlanningRecords[recordIndex]?.getField(
                                                columnIndex
                                            )?.currentValue
                                        }
                                        renderRowActions={recordIndex => (
                                            <div className={styles.rowAction}>{recordIndex}</div>
                                        )}
                                        renderPoint={record => (
                                            <div className={styles.pointWrapper}>
                                                <ModernTooltip
                                                    tooltip={texts.cust_initials_tooltip}
                                                    arrowOffset={8}
                                                    position="top-start"
                                                    tooltipOffset={8}
                                                >
                                                    <Point
                                                        text={getABBRlabel(
                                                            filteredPlanningRecords[
                                                                record.recordIndex
                                                            ],
                                                            plannedColumnsIds[
                                                                ColumnsNamesForMarkers.CUSTDES
                                                            ]
                                                        )}
                                                    />
                                                </ModernTooltip>
                                            </div>
                                        )}
                                        showFilter={true}
                                    />
                                </WidthController>
                            </div>
                        )}
                        {isPlanningLoading || isLoadingEditingFields ? (
                            <CircularLoader />
                        ) : (
                            <AvailableStatusesWrapper
                                form={routesForm}
                                records={filteredRoutesRecords}
                            >
                                <div
                                    className={classNames(styles.block, styles.tableBlock)}
                                    id={DELIVERY_BLOCK}
                                >
                                    <RoutesHeader
                                        form={routesForm}
                                        portalId={DELIVERY_BLOCK}
                                        setIsScreenLocked={setIsScreenLocked}
                                        selectedRoutes={routes.filter(r => r.isSelected)}
                                        onOpenEditDialog={onOpenFieldsEditor}
                                        toggleHideRoutes={toggleHideRoutes}
                                        isShowAll={showAll}
                                        setExpandMode={setExpandMode}
                                        isTableOptionDisabled={!cachedDeliveryColumns}
                                        docTypeFilter={docTypeFilter}
                                        setDocTypeFilter={setDocTypeFilter}
                                        unselectAllRoutes={unselectAllRoutes}
                                        isShowAllTotals={isShowAllTotals}
                                        onToggleShowTotalsForAll={onToggleShowTotalsForAll}
                                        zIndex={zIndex}
                                        recordsCounter={
                                            routes?.filter(r => r.shouldDisplay)?.length || 0
                                        }
                                        setOptimizationDialogOpenValue={
                                            setOptimizationDialogOpenValue
                                        }
                                    />

                                    {filteredRoutesRecords?.length ? (
                                        <div
                                            className={classNames(styles.routesFullContent, {
                                                [styles.contentShowTotals]:
                                                    routeTileTotalsEditEntity && isShowAllTotals
                                            })}
                                        >
                                            <WidthController
                                                widthDictionary={routeTableColsWidth}
                                                storeKey={ROUTE_TABLE_COLS_WIDTH_DICT_STORE_KEY}
                                            >
                                                <RoutesWrapper>
                                                    <Flex
                                                        direction={'column'}
                                                        componentClasses={styles.routesWrapper}
                                                    >
                                                        {routes
                                                            ?.filter(r => r.shouldDisplay)
                                                            .map(route => (
                                                                <RouteTile
                                                                    key={`{${route.id}${route.data.clientKey}`}
                                                                    route={route}
                                                                    portalId={DELIVERY_BLOCK}
                                                                    isRouteTileLoading={
                                                                        isRouteTileLoading
                                                                    }
                                                                    setIsRouteTileLoading={
                                                                        setIsRouteTileLoading
                                                                    }
                                                                    isRouteReloading={
                                                                        isRouteReloading
                                                                    }
                                                                    setIsRouteReloading={
                                                                        setIsRouteReloading
                                                                    }
                                                                    setIsScreenLocked={
                                                                        setIsScreenLocked
                                                                    }
                                                                    isSelected={route.isSelected}
                                                                    isRouteGroupLoading={
                                                                        isRouteGroupLoading
                                                                    }
                                                                    setIsRouteGroupLoading={
                                                                        setIsRouteGroupLoading
                                                                    }
                                                                    toggleRouteSelect={
                                                                        toggleRouteSelect
                                                                    }
                                                                    titleColumns={
                                                                        routesFormEditEntity.selectedFields as ColumnsNamesForRoutes[]
                                                                    }
                                                                    totalsColumns={
                                                                        routeTileTotalsEditEntity.selectedFields as ColumnsNamesForRoutes[]
                                                                    }
                                                                    tableColumns={
                                                                        routesTableEditEntity?.selectedFields ||
                                                                        []
                                                                    }
                                                                    cachedColumns={
                                                                        cachedDeliveryColumns
                                                                    }
                                                                    setDeliveryColumnsSetting={
                                                                        setDeliveryColumnsSetting
                                                                    }
                                                                    docTypeFilter={docTypeFilter}
                                                                    mapSettings={mapSettings}
                                                                />
                                                            ))}
                                                    </Flex>
                                                </RoutesWrapper>
                                            </WidthController>
                                            {routeTileTotalsEditEntity && isShowAllTotals && (
                                                <RoutesTotals
                                                    records={routes
                                                        ?.filter(r => r.shouldDisplay)
                                                        .map(i => i.data)}
                                                    totalsEntity={routeTileTotalsEditEntity}
                                                />
                                            )}
                                        </div>
                                    ) : routesFormFilters?.length ? (
                                        <EmptyState
                                            icon={<NotFoundRoutes />}
                                            title={
                                                <Text skin="paragraph-1">
                                                    {texts.not_found_routes}
                                                </Text>
                                            }
                                        />
                                    ) : (
                                        <Scrollbar
                                            rtl={isRtl}
                                            autoHeightMax={scrollMaxHeight}
                                            autoHeight
                                        >
                                            {routesFormFilters?.length ? (
                                                <EmptyState
                                                    containerClasses={styles.emptyStateContainer}
                                                    wrapperStyle={{
                                                        height: scrollMaxHeightWithOutHeader
                                                    }}
                                                    icon={<NotFoundRoutes />}
                                                    title={
                                                        <Text skin="paragraph-1">
                                                            {texts.not_found_routes}
                                                        </Text>
                                                    }
                                                />
                                            ) : (
                                                <EmptyState
                                                    containerClasses={styles.emptyStateContainer}
                                                    wrapperStyle={{
                                                        height: scrollMaxHeightWithOutHeader
                                                    }}
                                                    icon={<EmptyRoutes />}
                                                    title={getRoutesEmptyStateTitle()}
                                                />
                                            )}
                                        </Scrollbar>
                                    )}
                                </div>
                            </AvailableStatusesWrapper>
                        )}
                        <div
                            className={classNames(styles.block, styles.tableBlock, styles.mapBlock)}
                        >
                            {lang && (
                                <APIProvider language={lang} apiKey={googleMapsApiKey}>
                                    <MapWithDnd
                                        mapID={mapID}
                                        id={MovingLocation.map}
                                        selectedLocations={prepareRowsToMarkersType(
                                            selectedPlannedRows,
                                            plannedColumnsIds
                                        )}
                                        isShowLocations={isShowPlannedLocations}
                                        setIsScreenLocked={setIsScreenLocked}
                                        routes={routes}
                                        setRoutes={setRoutes}
                                        mapSettings={mapSettings}
                                        setMapSettings={setMapSettings}
                                    />
                                </APIProvider>
                            )}
                        </div>
                    </DNDController>
                    {isOpenEditFieldsDlg && (
                        <AdvancedDeliveryFieldsEditor
                            isLoading={isLoadingEditingFields}
                            updateFields={updateFields}
                            isOpen={isOpenEditFieldsDlg}
                            closeDialog={handleCloseEditFieldsDlg}
                            entity={getEntityByType()}
                        />
                    )}
                    <OptimizationDialog
                        ref={optimizationDialogRef}
                        zIndex={zIndex}
                        openValue={optimizationDialogOpenValue}
                        setOpenValue={setOptimizationDialogOpenValue}
                        optimizationDialogState={optimizationDialogState}
                        setOptimizationDialogState={setOptimizationDialogState}
                        assignToDeliveryHandler={assignToDeliveryHandler}
                        planningForm={planningForm}
                        routesForm={routesForm}
                        routes={routes}
                        session={session}
                        dname={dname}
                        processBatchRoutesReloadData={processBatchRoutesReloadData}
                        selectedPlannedRows={selectedPlannedRows}
                        selectedRoutes={selectedRoutes}
                        optimizationLoaderState={optimizationLoaderState}
                        setOptimizationLoaderState={setOptimizationLoaderState}
                        setIsScreenLocked={setIsScreenLocked}
                        reloadData={async (openValue, ids) => {
                            setIsScreenLocked(true);
                            await reloadForms(true);
                            if (openValue === OptimizationDialogOpenValue.Open) {
                                await reloadSpecificRouteTables(ids);
                            } else if (openValue === OptimizationDialogOpenValue.UnassignedOpen) {
                                const shouldReload = routes
                                    .filter(route => !!route.locationSubform)
                                    .map(
                                        route =>
                                            route.data.getField(
                                                routesForm.getColumnByName('DLVTRACKNUM')?.id
                                            )?.currentValue as string
                                    );
                                if (shouldReload.length > 0) {
                                    setReloadingRoutesLocker(true);
                                    await reloadSpecificRouteTables(shouldReload);
                                }
                            }
                            setIsScreenLocked(false);
                        }}
                    />
                    {optimizationLoaderState !== OptimizationLoaderState.Hidden && (
                        <OptimizationLoader
                            zIndex={zIndex + 1}
                            title={texts.optimization_loader_title}
                            disableCancel={
                                optimizationLoaderState !==
                                OptimizationLoaderState.VisibleCancellable
                            }
                            cancelButtonText={texts.cancel_btn}
                            description={texts.optimization_loader_description}
                            cancelTooltip={texts.optimization_loader_cancel_tooltip}
                            onCloseProceed={(
                                obj => () =>
                                    optimizationLoaderOnCloseProceed(obj)
                            )(optimizationDialogRef.current)}
                        />
                    )}
                    {showCloseWarning && (
                        <OptimizationCancelDialog
                            zIndex={zIndex + 1}
                            optimizationLoaderState={optimizationLoaderState}
                            open={showCloseWarning}
                            setOpen={setShowCloseWarning}
                            onCloseProceed={handleClose}
                        />
                    )}
                </div>
            </div>
        );
    }
);
